import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment from 'moment-timezone'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import AnimatedTableChevron from 'src/components/AnimatedTableChevron/AnimatedTableChevron'
import Box from 'src/components/Box/Box'
import LoadingBoxBottom from 'src/components/LoadingBoxBottom/LoadingBoxBottom'
import NoPeople from 'src/components/NoPeople/NoPeople'
import ObjectTable from 'src/components/ObjectTable/ObjectTable'
import ObjectTableBodyCell from 'src/components/ObjectTableBodyCell/ObjectTableBodyCell'
import ObjectTableBodyRow from 'src/components/ObjectTableBodyRow/ObjectTableBodyRow'
import ObjectTableHeaderRow from 'src/components/ObjectTableHeaderRow/ObjectTableHeaderRow'
import SortableTableHeader, {
  SortableTableHeaderAlignment,
} from 'src/components/SortableTableHeader/SortableTableHeader'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes, OrganizationRoles } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { OrgInviteModel } from 'src/models/people.models'
import Lnk from 'src/navigation/Lnk'
import {
  peopleCancelOrgInviteUrl,
  peopleInvitedToOrgDetailUrl,
  peopleInvitedToOrgUrl,
} from 'src/navigation/urls'
import { createSetIsCancelOrgInvitePopupOpen } from 'src/redux/actions/app.actions'
import { postInviteToOrganizationAndOpenToastAndCloseFlyout } from 'src/redux/actions/data/organizations.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import styled, { css } from 'styled-components'

const CenteredObjectTableBodyCell = styled(ObjectTableBodyCell)`
  vertical-align: middle;
`

interface PeopleInvitedToOrgListProps {
  invites: OrgInviteModel[]
  loading?: boolean
  sortableProps: {
    setSort: (field: string) => void
    sortBy?: string
    sortDescending?: boolean
  }
}

enum sortValues {
  name = 'firstName',
  status = 'status',
  knownSince = 'createdAt',
}

// placeholder component.  Will drastically change once invite to org component is implemented
const PeopleInvitedToOrgList: React.FC<PeopleInvitedToOrgListProps> = (
  props,
) => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)
  const { analytics } = useAnalyticsContext()

  const userOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const dispatch = useFlyoutDispatch()
  const dispatchPostUserInvites = (invite: OrgInviteModel): void => {
    analytics?.track(
      'people invited to org list - reinvite to org - text link press',
      {
        screen: '/people/invited',
        interaction: 'text link press',
        intent: 'reinvite to org',
      },
    )
    dispatch(
      postInviteToOrganizationAndOpenToastAndCloseFlyout(
        userOrganization?.id || '',
        {
          emails: [invite.invitee.email],
          role: invite.role,
          bookkeeperAccountVisibilityType:
            invite.role === OrganizationRoles.bookkeeper
              ? invite.accountVisibility?.type
              : undefined,
          bookkeeperAccounts:
            invite.role === OrganizationRoles.bookkeeper
              ? invite.accountVisibility?.ids
              : undefined,
          cardManagerAccounts:
            invite.role === OrganizationRoles.cardManager
              ? invite.accountVisibility?.ids
              : undefined,
        },
        false,
        false,
        true,
      ),
    )
  }

  const dispatchPostUserInvitesCancel = (): void => {
    analytics?.track(
      'people invited to org list - cancel invite to org - text link press',
      {
        screen: '/people/invited',
        interaction: 'text link press',
        intent: 'cancel invite to org',
      },
    )
    dispatch(createSetIsCancelOrgInvitePopupOpen(true))
  }

  // locale
  useEffect(() => {
    if (locale !== undefined) {
      moment.locale(locale)
    }
  }, [locale])

  return (
    <Box orgBookkeeper noShadow noPadding>
      <ObjectTable>
        {props.invites.length > 0 && (
          <ObjectTableHeaderRow>
            <SortableTableHeader
              css={css`
                width: 38%;
              `}
              alignment={SortableTableHeaderAlignment.LeftAligned}
              label={translate('Name')}
              onClick={
                props.sortableProps
                  ? (): void => {
                      props.sortableProps?.setSort(sortValues.name)
                      analytics?.track(
                        'people invited to org list - sort people - table header press',
                        {
                          screen: '/people/invited',
                          interaction: 'table header press',
                          intent: 'sort people',
                          sortField: sortValues.name,
                          sortDirection: props.sortableProps?.sortDescending
                            ? 'DESC'
                            : 'ASC',
                        },
                      )
                    }
                  : undefined
              }
              isSorted={props.sortableProps?.sortBy === sortValues.name}
              sortDescending={props.sortableProps?.sortDescending}
            />
            <SortableTableHeader
              css={css`
                width: 31%;
              `}
              alignment={SortableTableHeaderAlignment.LeftAligned}
              label={translate('Status')}
              onClick={
                props.sortableProps
                  ? (): void => {
                      props.sortableProps?.setSort(sortValues.status)
                      analytics?.track(
                        'people invited to org list - sort people - table header press',
                        {
                          screen: '/people/invited',
                          interaction: 'table header press',
                          intent: 'sort people',
                          sortField: sortValues.status,
                          sortDirection: props.sortableProps?.sortDescending
                            ? 'DESC'
                            : 'ASC',
                        },
                      )
                    }
                  : undefined
              }
              isSorted={props.sortableProps?.sortBy === sortValues.status}
              sortDescending={props.sortableProps?.sortDescending}
            />
            <SortableTableHeader
              css={css`
                width: 12%;
              `}
              alignment={SortableTableHeaderAlignment.LeftAligned}
              label={translate('Invited On')}
              onClick={
                props.sortableProps
                  ? (): void => {
                      props.sortableProps?.setSort(sortValues.knownSince)
                      analytics?.track(
                        'people invited to org list - sort people - table header press',
                        {
                          screen: '/people/invited',
                          interaction: 'table header press',
                          intent: 'sort people',
                          sortField: sortValues.knownSince,
                          sortDirection: props.sortableProps?.sortDescending
                            ? 'DESC'
                            : 'ASC',
                        },
                      )
                    }
                  : undefined
              }
              isSorted={props.sortableProps?.sortBy === sortValues.knownSince}
              sortDescending={props.sortableProps?.sortDescending}
            />
            <SortableTableHeader
              css={css`
                width: 15%;
              `}
              alignment={SortableTableHeaderAlignment.LeftAligned}
            />
          </ObjectTableHeaderRow>
        )}
        {props.invites.length > 0 &&
          props.invites.map((invite, i) => {
            const { firstName, lastName, email, verified, id } = invite.invitee

            return (
              <ObjectTableBodyRow
                key={i}
                to={peopleInvitedToOrgDetailUrl(id)}
                ariaLabelSource={'People Details'}
              >
                <CenteredObjectTableBodyCell>
                  <>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                      `}
                      key={i}
                      onClick={(): void => {
                        analytics?.track(
                          'people invited to org list - open user detail - list item press',
                          {
                            screen: '/people/invited',
                            interaction: 'list item press',
                            intent: 'open user detail',
                            viewedUserId: id,
                          },
                        )
                      }}
                      onKeyPress={(): null => {
                        return null
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <UserAvatar
                        css={css`
                          margin-right: 10px;
                        `}
                        user={invite.invitee}
                        size={AvatarSizes.Small}
                      />
                      <div
                        css={css`
                          display: flex;
                          flex-direction: column;
                        `}
                      >
                        <span
                          css={css`
                            ${getFontCss(
                              16,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.medium,
                            )}
                          `}
                        >
                          {firstName} {lastName}
                        </span>
                        <span
                          css={css`
                            ${getFontCss(
                              12,
                              styleConstants.colors.gray_darkest,
                              styleConstants.fontFamilies.regular,
                            )}
                          `}
                        >
                          {email}
                        </span>
                      </div>
                    </div>
                  </>
                </CenteredObjectTableBodyCell>
                <CenteredObjectTableBodyCell>
                  {verified ? (
                    <div
                      css={css`
                        ${getFontCss(
                          14,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.regular,
                        )}
                      `}
                    >
                      {translate('Registered')}
                    </div>
                  ) : (
                    <div
                      css={css`
                        ${getFontCss(
                          14,
                          styleConstants.colors.gray_dark,
                          styleConstants.fontFamilies.regular,
                        )}
                      `}
                    >
                      {translate('Unregistered')}
                    </div>
                  )}
                </CenteredObjectTableBodyCell>
                <CenteredObjectTableBodyCell>
                  {moment(invite.createdAt).format('L')}
                </CenteredObjectTableBodyCell>
                <CenteredObjectTableBodyCell>
                  <span
                    css={css`
                      display: flex;
                      flex-direction: row;
                    `}
                  >
                    <Lnk
                      css={css`
                        ${getFontCss(
                          14,
                          themeConstants.colors.cta,
                          styleConstants.fontFamilies.regular,
                        )};
                        text-decoration: underline;
                        :hover {
                          ${getFontCss(
                            14,
                            themeConstants.colors.cta,
                            styleConstants.fontFamilies.medium,
                          )};
                        }
                      `}
                      to={peopleInvitedToOrgUrl()}
                      onClick={(): void => dispatchPostUserInvites(invite)}
                      ariaLabelSource={'reinvite'}
                    >
                      {translate('Reinvite')}
                    </Lnk>
                    <Lnk
                      css={css`
                        margin-left: 15px;
                        ${getFontCss(
                          14,
                          styleConstants.colors.red,
                          styleConstants.fontFamilies.regular,
                        )};
                        text-decoration: underline;
                        :hover {
                          ${getFontCss(
                            14,
                            styleConstants.colors.red,
                            styleConstants.fontFamilies.medium,
                          )};
                        }
                      `}
                      to={peopleCancelOrgInviteUrl(invite.id)}
                      onClick={(): void => dispatchPostUserInvitesCancel()}
                      ariaLabelSource={'cancel'}
                    >
                      {translate('Cancel')}
                    </Lnk>
                  </span>
                </CenteredObjectTableBodyCell>
                <CenteredObjectTableBodyCell>
                  <AnimatedTableChevron />
                </CenteredObjectTableBodyCell>
              </ObjectTableBodyRow>
            )
          })}
      </ObjectTable>
      {props.loading ? (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            flex-grow: 2;
          `}
        >
          <LoadingBoxBottom />
        </div>
      ) : (
        <div
          css={css`
            display: flex;
            justify-content: center;
          `}
        >
          {props.invites.length === 0 && (
            <NoPeople
              titleText={translate('No one matched your search.')}
              topSubtitleText={
                <div
                  css={css`
                    flex-direction: column;
                  `}
                >
                  {translate('Check the keyword you typed for any errors.')}
                </div>
              }
            />
          )}
        </div>
      )}
    </Box>
  )
}

export default PeopleInvitedToOrgList
